import { BookOpen, CandlestickChart, Building2, Timer, ShieldCheck } from "lucide-react";

export const COURSE_NAME = "Complete Stock Market Course (A to Z)";
export const ORIGINAL_PRICE = 9999;
export const DISCOUNT = 50;
export const PRICE = 4999;
export const NEXT_BATCH = "Sunday, 1 November 2026";
export const SEATS_LEFT = 20;
export const BATCH_START_ISO = "2026-11-01T00:00:00+05:30";
export const PHONE_DISPLAY = "+91 72768 63734";
export const PHONE_TEL = "tel:+917276863734";
export const INSTAGRAM_URL = "https://www.instagram.com/rlt_pune?dlrf=emwwZ2c4dWt1cHFn";
export const WHATSAPP_URL = `https://wa.me/919185063734?text=${encodeURIComponent(
  `Hi RL Trading Institute, I want to enroll in the ${COURSE_NAME}.`
)}`;

export const modules = [
  {
    icon: BookOpen,
    title: "Basics of Stock Market",
    tagline: "Build a rock-solid foundation before you place a single trade.",
    summary:
      "We start from zero. You will understand how the stock market really works, who the participants are, and how money flows between them — so every concept that follows makes complete sense.",
    topics: [
      "What is a share, stock exchange (NSE / BSE) and how SEBI regulates the market",
      "Primary vs secondary market, IPOs and how prices are discovered",
      "Demat & trading accounts, brokers, order types (market, limit, stop-loss, GTT)",
      "Market participants: retail traders, FIIs, DIIs, HNIs and market makers",
      "Indices like NIFTY 50, BANK NIFTY & SENSEX and what moves them",
      "Equity vs F&O basics — cash, futures and options explained simply",
      "Market timings, pre-open session, circuits and settlement cycle (T+1)",
    ],
    outcome: "Confidently navigate any trading platform and understand what happens behind every price move.",
  },
  {
    icon: CandlestickChart,
    title: "Types of Candles and Chart Patterns",
    tagline: "Learn to read the language of price.",
    summary:
      "Every candle tells a story of the battle between buyers and sellers. In this module you learn to read that story and recognise the patterns that repeat again and again across all timeframes.",
    topics: [
      "Anatomy of a candlestick — open, high, low, close, body and wicks",
      "Single candle patterns: Doji, Hammer, Shooting Star, Marubozu, Spinning Top",
      "Multi-candle patterns: Engulfing, Harami, Morning Star, Evening Star, Tweezer",
      "Trend, support & resistance and market structure (HH, HL, LH, LL)",
      "Reversal patterns: Head & Shoulders, Double Top / Bottom, Triple Top",
      "Continuation patterns: Flags, Pennants, Triangles, Wedges and Rectangles",
      "Multi-timeframe analysis and avoiding false breakouts",
    ],
    outcome: "Open any chart and instantly identify trend, key levels and high-probability patterns.",
  },
  {
    icon: Building2,
    title: "Institutional Zones (Supply / Demand Zones)",
    tagline: "Trade where the big money trades.",
    summary:
      "Banks and institutions leave footprints on the chart. This module teaches you how to spot those footprints — the supply and demand zones where large orders sit — and how to position yourself alongside smart money.",
    topics: [
      "How institutional orders move price and why retail traders get trapped",
      "Identifying fresh supply and demand zones — Rally-Base-Drop, Drop-Base-Rally and more",
      "Strength of a zone: departure, time at base, freshness and higher-timeframe confluence",
      "Marking zones correctly — proximal and distal lines",
      "Entry, stop-loss and target planning from zones",
      "Liquidity grabs, stop hunts and fake breakouts",
      "Live chart walkthroughs on stocks, indices and options",
    ],
    outcome: "Mark professional-grade zones and plan trades with clear entries, stops and targets.",
  },
  {
    icon: Timer,
    title: "Intraday and Swing Trading Setups",
    tagline: "Rule-based setups you can repeat every day.",
    summary:
      "Theory becomes profit only with a clear, repeatable process. Here you learn complete trading setups for both intraday and swing trading — with exact entry, exit and trade-management rules.",
    topics: [
      "Choosing the right stocks — volume, volatility and watchlist building",
      "Intraday setups: opening range, breakout-retest, EMA-based and zone-based entries",
      "Swing setups: trend pullbacks, zone re-tests and multi-day breakouts",
      "Using moving averages (EMA), volume and VWAP as confirmation",
      "Pre-market preparation and a daily trading routine",
      "Trade management — trailing stops, partial booking and exits",
      "Backtesting a setup and maintaining a trading journal",
    ],
    outcome: "Walk away with a ready trading plan for both intraday and swing positions.",
  },
  {
    icon: ShieldCheck,
    title: "Risk Management and Trading Psychology",
    tagline: "The part that separates consistent traders from the rest.",
    summary:
      "Most traders don't fail because of bad strategy — they fail because of poor risk control and emotions. This module gives you the rules and the mindset to protect your capital and stay consistent.",
    topics: [
      "Capital protection first — the 1–2% risk-per-trade rule",
      "Position sizing and calculating quantity from your stop-loss",
      "Risk-to-reward ratio, win rate and expectancy",
      "Daily / weekly loss limits and avoiding overtrading",
      "Handling fear, greed, FOMO and revenge trading",
      "Building discipline, patience and a winning routine",
      "Reviewing trades and continuous improvement",
    ],
    outcome: "Trade with discipline, control your losses and grow your account steadily.",
  },
];
